// src/components/SiteConfig/SiteConfig.tsx
import React, { useState, useEffect } from 'react'
import { Form, Button } from 'react-bootstrap'
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons'
import { toast } from 'react-toastify'
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner'
import {
  updateSiteUrl,
  updateSitePhone,
  updateLocalDescription,
  toggleShowSite,
  toggleShowPhone,
  toggleShowCoordinates,
  toggleShowReservations,
  toggleShowPromotions,
  toggleShowMenu,
  toggleShowPdfMenu,
  toggleShowDescription,
} from '../../../../../../crud/management_api/local_site_configuration'
import Coordinates from './components/Coordinates/Coordinates'
import Promotions from './components/Promotions/Promotions'
import MenuViewer from './components/MenuViewer/MenuViewer'
import './SiteConfig.scss'
import { LocalSiteConfig } from './types'
import {
  FaWhatsapp,
  FaCalendar,
  FaLink,
  FaLocationArrow,
  FaTicketAlt,
  FaList,
  FaFileContract,
  FaInfoCircle,
  FaCheck
} from 'react-icons/fa'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

interface Props {
  config: LocalSiteConfig
  setConfig: React.Dispatch<React.SetStateAction<LocalSiteConfig>>
  /** Optional callback fired after a successful change so previews can reload */
  onConfigChanged?: () => void
}

type ToggleKey =
  | 'show_description'
  | 'show_site'
  | 'show_phone'
  | 'show_coordinates'
  | 'show_reservations'
  | 'show_promotions'
  | 'show_menu'
  | 'show_pdf_menu'

const toggleDefs = [
  { label: 'Mostrar descripción', key: 'show_description', icon: <FaInfoCircle /> },
  { label: 'Mostrar página web', key: 'show_site', icon: <FaLink /> },
  { label: 'Habilitar WhatsApp', key: 'show_phone', icon: <FaWhatsapp /> },
  { label: 'Mostrar como llegar', key: 'show_coordinates', icon: <FaLocationArrow /> },
  { label: 'Reservas', key: 'show_reservations', icon: <FaCalendar /> },
  { label: 'Mostrar promociones', key: 'show_promotions', icon: <FaTicketAlt /> },
  { label: 'Menú integrado', key: 'show_menu', icon: <FaList /> },
  { label: 'PDF menú', key: 'show_pdf_menu', icon: <FaFileContract /> },
] as const

export default function SiteConfig({ config, setConfig, onConfigChanged }: Props) {
  const [url, setUrl] = useState(config.site_url || '')
  const [origUrl] = useState(config.site_url || '')
  const [savingUrl, setSavingUrl] = useState(false)

  const [phone, setPhone] = useState(config.site_phone || '')
  const [origPhone] = useState(config.site_phone || '')
  const [savingPhone, setSavingPhone] = useState(false)

  const [description, setDescription] = useState(config.local_description || '')
  const [origDescription] = useState(config.local_description || '')
  const [savingDescription, setSavingDescription] = useState(false)

  const [togglesLoading, setTogglesLoading] = useState<Record<ToggleKey, boolean>>({} as any)
  const [expanded, setExpanded] = useState<Record<ToggleKey, boolean>>(() =>
    toggleDefs.reduce((acc, { key }) => {
      acc[key] = false
      return acc
    }, {} as Record<ToggleKey, boolean>)
  )

  // nuevo estado para descripción
  const [descExpanded, setDescExpanded] = useState(false)

  useEffect(() => {
    setUrl(config.site_url || '')
    setPhone(config.site_phone || '')
    setDescription(config.local_description || '')
  }, [config.site_url, config.site_phone, config.local_description])

const mg = async <K extends ToggleKey>(key: K, fn: (local_id: any) => Promise<any>) => {
  setTogglesLoading(t => ({ ...t, [key]: true }));
  try {
    const user = localStorage.getItem('user');
    const localId = user ? JSON.parse(user).local_id : null;

    // Sincronización mutua entre Menú Integrado y Menú PDF
    if (key === 'show_menu') {
      if (!config.show_menu) {
        // Estoy activando el Menú Integrado → desactivo PDF si estaba activo
        if (config.show_pdf_menu) {
          await toggleShowPdfMenu(localId);
          setConfig(c => ({ ...c, show_pdf_menu: false }));
          toast.info('El menú PDF se ha desactivado porque activaste el Menú Integrado.');
        }
      } else {
        // Estoy desactivando el Menú Integrado → activo PDF si estaba desactivado
        if (!config.show_pdf_menu) {
          await toggleShowPdfMenu(localId);
          setConfig(c => ({ ...c, show_pdf_menu: true }));
          toast.info('El Menú PDF se ha activado porque desactivaste el Menú Integrado.');
        }
      }
    }

    if (key === 'show_pdf_menu') {
      if (!config.show_pdf_menu) {
        // Estoy activando el Menú PDF → desactivo Integrado si estaba activo
        if (config.show_menu) {
          await toggleShowMenu(localId);
          setConfig(c => ({ ...c, show_menu: false }));
          toast.info('El Menú Integrado se ha desactivado porque activaste el Menú PDF.');
        }
      } else {
        // Estoy desactivando el Menú PDF → activo Integrado si estaba desactivado
        if (!config.show_menu) {
          await toggleShowMenu(localId);
          setConfig(c => ({ ...c, show_menu: true }));
          toast.info('El Menú Integrado se ha activado porque desactivaste el Menú PDF.');
        }
      }
    }

    // Ejecuta el toggle principal y actualiza estado
  await fn(localId);
    setConfig(c => ({ ...c, [key]: !c[key] }));
    setExpanded(e => ({ ...e, [key]: !e[key] }));
  onConfigChanged?.();
  } catch {
    toast.error('Error al cambiar estado');
  } finally {
    setTogglesLoading(t => ({ ...t, [key]: false }));
  }
};

  const saveUrl = async () => {
    setSavingUrl(true)
    try {
      const user = localStorage.getItem('user')
      const localId = user ? JSON.parse(user).local_id : null
      await updateSiteUrl(localId, url)
  toast.success('URL guardada')
      setConfig(c => ({ ...c, site_url: url }))
  onConfigChanged?.()
    } catch {
      toast.error('Error al guardar URL')
    } finally {
      setSavingUrl(false)
    }
  }

  const savePhone = async () => {
    setSavingPhone(true)
    try {
      const user = localStorage.getItem('user')
      const localId = user ? JSON.parse(user).local_id : null
      await updateSitePhone(localId, phone)
  toast.success('Teléfono guardado')
      setConfig(c => ({ ...c, site_phone: phone }))
  onConfigChanged?.()
    } catch {
      toast.error('Error al guardar teléfono')
    } finally {
      setSavingPhone(false)
    }
  }

  const saveDescription = async () => {
    setSavingDescription(true)
    try {
      const user = localStorage.getItem('user')
      const localId = user ? JSON.parse(user).local_id : null
      await updateLocalDescription(localId, description)
  toast.success('Descripción guardada')
      setConfig(c => ({ ...c, local_description: description }))
  onConfigChanged?.()
    } catch {
      toast.error('Error al guardar descripción')
    } finally {
      setSavingDescription(false)
    }
  }

  if (!config) return <MiniLoadingSpinner />

  return (
    <div className="site-config-container">
      <h3>Modificaciones y permisos de Mi punto digital</h3>
      <Form className="config-form">

        {/* — Sección Descripción con chevron toggle — */}
        <div className="form-row config-digital-point">
          <div className="label-with-icon">
            <Form.Label>
              <span className="toggle-icon"><FaInfoCircle /></span>
              Descripción del local
            </Form.Label>
            <Button
              variant="link"
              className="chevron-inline"
              onClick={() => setDescExpanded(prev => !prev)}
            >
              {descExpanded ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </div>
        </div>
        {descExpanded && (
          <div className="form-row fold-panel">
            <div className="url-input">
              <Form.Control
                as="textarea"
                rows={1}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe tu local..."
              />
              {description !== origDescription && (
                <Button
                  variant="outline-primary"
                  onClick={saveDescription}
                  disabled={savingDescription}
                >
                  {savingDescription ? <MiniLoadingSpinner size="small" /> : <FaCheck />}
                </Button>
              )}
            </div>
          </div>
        )}
        {/* ———————————————————————————————— */}

        {toggleDefs.map(({ label, key, icon }) => {
          const fn = {
            show_description: toggleShowDescription,
            show_site: toggleShowSite,
            show_phone: toggleShowPhone,
            show_coordinates: toggleShowCoordinates,
            show_reservations: toggleShowReservations,
            show_promotions: toggleShowPromotions,
            show_menu: toggleShowMenu,
            show_pdf_menu: toggleShowPdfMenu,
          }[key] as (id: number) => Promise<any>

          const hasPanel = ['show_site','show_phone','show_coordinates','show_promotions','show_pdf_menu'].includes(key)

          const menuInfo = key === 'show_menu' ? (
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="tooltip-menu-integrado">
                  El menú integrado muestra tu carta generada directamente con Medio Punto en la web
                </Tooltip>
              }
            >
              <span className="info-icon" style={{ marginLeft: '10px' }}><FaInfoCircle /></span>
            </OverlayTrigger>
          ) : null

          return (
            <React.Fragment key={key}>
              <div className="form-row config-digital-point">
                <div className="label-with-icon">
                  <Form.Label>
                    <span className="toggle-icon">{icon}</span>
                    {label}
                    {menuInfo}
                  </Form.Label>
                  {hasPanel && config[key] && (
                    <Button
                      variant="link"
                      className="chevron-inline"
                      onClick={() =>
                        setExpanded(e => ({ ...e, [key]: !e[key] }))
                      }
                    >
                      {expanded[key] ? <ChevronUp /> : <ChevronDown />}
                    </Button>
                  )}
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={config[key]}
                    disabled={togglesLoading[key]}
                    onChange={() => mg(key, fn)}
                  />
                  <span className="slider round" />
                </label>
              </div>

              {key === 'show_site' && config.show_site && expanded.show_site && (
                <div className="form-row fold-panel">
                  <Form.Label>URL pública</Form.Label>
                  <div className="url-input">
                    <Form.Control
                      type="text"
                      value={url}
                      onChange={e => setUrl(e.target.value)}
                      placeholder="https://…"
                      className="config-menu-input-url"
                    />
                    {url !== origUrl && (
                      <Button
                        variant="outline-primary"
                        onClick={saveUrl}
                        disabled={savingUrl}
                      >
                        {savingUrl ? <MiniLoadingSpinner size="small" /> : <FaCheck />}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {key === 'show_phone' && config.show_phone && expanded.show_phone && (
                <div className="form-row fold-panel">
                  <Form.Label>Teléfono</Form.Label>
                  <div className="url-input">
                    <Form.Control
                      type="tel"
                      inputMode="tel"
                      pattern="[0-9()+\s]*"
                      value={phone}
                      onKeyPress={e => {
                        if (!/[0-9()+\s]/.test(e.key)) e.preventDefault()
                      }}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="+56 9 1234 5678"
                    />
                    {phone !== origPhone && (
                      <Button
                        variant="outline-primary"
                        onClick={savePhone}
                        disabled={savingPhone}
                      >
                        {savingPhone ? <MiniLoadingSpinner size="small" /> : <FaCheck />}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {key === 'show_coordinates' && config.show_coordinates && expanded.show_coordinates && (
                <Coordinates config={config} setConfig={setConfig} />
              )}
              
              {key === 'show_promotions' && config.show_promotions && expanded.show_promotions && (
                <Promotions config={config} setConfig={setConfig} />
              )}

              {key === 'show_pdf_menu' && config.show_pdf_menu && expanded.show_pdf_menu && (
                <MenuViewer config={config} />
              )}
            </React.Fragment>
          )
        })}
      </Form>
    </div>
  )
}
